import { Clock, CheckCircle, DollarSign, Home, ThumbsUp, ShieldCheck, ScanLine, Calendar } from 'lucide-react';

import Head from 'next/head';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Split from '../components/Split';
import CTASection from '../components/CTASection';
import Benefits from '../components/Benefits';
import ContentBlock from '../components/ContentBlock';
import Services from '../components/Services';
import SocialProof from '../components/SocialProof';
import ContactUs from '../components/ContactUs';
import Footer from '../components/Footer';
import { generateWebPageSchema } from '../utils/generateWebPageSchema';

export default function Financing() {
  const base = 'https://modernbuildremodel.com';
  const pageUrl = `${base}/financing`;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": base },
      { "@type": "ListItem", "position": 2, "name": "Financing", "item": pageUrl }
    ]
  };

  return (
    <>
      <Head>
        <title>Flexible Financing Options | Modern Build</title>
        <meta name="title" content="Flexible Financing Options | Modern Build" />
        <meta name="description" content="Modern Build offers flexible, fast, and friendly financing options for your next remodeling project. Instant approvals, no prepayment penalties, and $0 down options available." />
        <meta name="keywords" content="home remodeling financing, construction loans, flexible payments, zero down, home improvement credit, los angeles remodeling financing, Modern Build finance" />
        <meta name="author" content="Modern Build Home Remodeling" />

        {/* Canonical URL to avoid duplicate content issues */}
        <link rel="canonical" href="https://modernbuildremodel.com/financing" />

        {/* Open Graph Meta Tags for social sharing */}
        <meta property="og:title" content="Flexible Financing Options | Modern Build" />
        <meta property="og:description" content="Easy payment plans. Fast approvals. No equity required. Explore financing options for kitchens, bathrooms, ADUs, roofing & more—tailored for LA homeowners." />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles-with-flexible-financing.webp" />
        <meta property="og:url" content="https://modernbuildremodel.com/financing" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Flexible Financing Options | Modern Build" />
        <meta name="twitter:description" content="Remodel now and pay later! Get $0 down, instant approval, and flexible financing up to 15 years. No equity needed. Perfect for kitchens, bathrooms, and more." />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles-with-flexible-financing.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />
      
         {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Flexible Home Remodeling Financing Options',
                description: 'Explore flexible financing options for home renovation and remodeling projects in Southern California.',
                slug: '/financing'
              })
            )
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      
      </Head>

      <Header />
      <Hero category="financing" page="page" hasContentH1={false}/>
      <Split
        subtitle="FLEXIBLE FINANCING"
        title="Home Improvement Made Affordable"
        background="white"
        description="We believe every homeowner deserves a beautiful, functional space — without the financial stress. That’s why we offer flexible financing options that make remodeling possible on any budget."
        extraText="With fast approvals, $0 down, and plans up to 15 years, we help turn your dream renovation into a reality — without draining your savings. No equity required. All credit scores welcome."
        phone="(323) 300-4130"
        image1="/split/best-los-angeles-ca-home-remodeling-contractor.webp"
        image2="/split/best-los-angeles-ca-bathroom-remodeling-contractor.webp"
        reverse={false}
      />
      <CTASection
        title="Remodel Now. Pay Later."
        subtitle="100% Financing Available with $0 Down*"
        buttonText="Get Pre-Approved Today"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />
      <Benefits
        title="Why Choose Financing?"
        subtitle="Flexible, fast, and designed to work for every homeowner."
        items={[
          {
            icon: <CheckCircle className="w-8 h-8 text-primary" />,
            title: "Instant Approval",
          },
          {
            icon: <Clock className="w-8 h-8 text-primary" />,
            title: "Simple 60-Second Application",
          },
          {
            icon: <DollarSign className="w-8 h-8 text-primary" />,
            title: "Loans Up To $250,000",
          },
          {
            icon: <Home className="w-8 h-8 text-primary" />,
            title: "Equity In Your Home Is Not Required",
          },
          {
            icon: <ThumbsUp className="w-8 h-8 text-primary" />,
            title: "All Credit Scores Welcome",
          },
          {
            icon: <ShieldCheck className="w-8 h-8 text-primary" />,
            title: "No Prepayment Penalties",
          },
          {
            icon: <ScanLine className="w-8 h-8 text-primary" />,
            title: "Comparing Your Options Won't Affect Your Credit Score",
          },
          {
            icon: <Calendar className="w-8 h-8 text-primary" />,
            title: "2–15 Year Payment Plans Available",
          },
        ]}
      />
      <ContentBlock
        subtitle="FINANCE YOUR PROJECT"
        title="100% Financing Available – No Equity Required"
        description="Modern Build partners with leading lenders to help homeowners bring their vision to life. With $0 down and no prepayment penalties, our flexible plans are designed to make your project affordable."
        extraText="Compare loan options in seconds without affecting your credit score. Approval takes just 60 seconds — and you can start building right away."
        buttonText="Get Started"
        background="white"
      />
      <Services category="home" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
}
